/**
 * My Series Page
 * Creator page for managing course series
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSeriesStore } from '../stores/seriesStore';
import { Series } from '../services/seriesService';
import './MySeriesPage.css';

const MySeriesPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['series', 'common']);
  const {
    mySeries,
    loading,
    error,
    fetchMySeries,
    createSeries,
    deleteSeries,
  } = useSeriesStore();

  const [isCreating, setIsCreating] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);

  useEffect(() => {
    fetchMySeries();
  }, [fetchMySeries]);

  const handleCreateSeries = async () => {
    if (!newTitle.trim()) {
      setCreateError(t('series:titleRequired'));
      return;
    }

    try {
      setCreateError(null);
      const series = await createSeries({
        title: newTitle.trim(),
        description: newDescription.trim() || undefined,
      });
      setNewTitle('');
      setNewDescription('');
      setIsCreating(false);
      navigate(`/creator/series/${series.id}`);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : t('series:createFailed'));
    }
  };

  const handleDeleteSeries = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm(t('series:deleteConfirm'))) {
      try {
        await deleteSeries(id);
      } catch (err) {
        console.error('Failed to delete series:', err);
      }
    }
  };

  const getCourseCount = (series: Series): number => {
    return series.courses?.length || series.courseCount || 0;
  };

  const getFirstCourseThumbnail = (series: Series): string | null => {
    const firstCourse = series.courses?.[0];
    return firstCourse?.coverImageUrl || firstCourse?.thumbnailUrl || null;
  };

  if (loading && mySeries.length === 0) {
    return (
      <div className="my-series-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>{t('series:loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="my-series-page">
      <div className="series-header">
        <h1>{t('series:title')}</h1>
        {!isCreating && (
          <button
            className="btn-create-series"
            onClick={() => setIsCreating(true)}
          >
            {t('series:createSeries')}
          </button>
        )}
      </div>

      {error && (
        <div className="error-banner">
          <p>{error}</p>
          <button onClick={() => fetchMySeries()}>{t('series:retry')}</button>
        </div>
      )}

      {isCreating && (
        <div className="create-series-form">
          <input
            type="text"
            placeholder={t('series:titlePlaceholder')}
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleCreateSeries();
              }
              if (e.key === 'Escape') {
                setIsCreating(false);
                setNewTitle('');
                setNewDescription('');
              }
            }}
            autoFocus
            maxLength={100}
          />
          <textarea
            placeholder={t('series:descriptionPlaceholder')}
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            rows={3}
            maxLength={500}
          />
          {createError && <p className="create-error">{createError}</p>}
          <div className="create-actions">
            <button
              className="btn-cancel"
              onClick={() => {
                setIsCreating(false);
                setNewTitle('');
                setNewDescription('');
                setCreateError(null);
              }}
            >
              {t('common:cancel')}
            </button>
            <button
              className="btn-save"
              onClick={handleCreateSeries}
              disabled={!newTitle.trim()}
            >
              {t('series:create')}
            </button>
          </div>
        </div>
      )}

      {mySeries.length === 0 && !isCreating ? (
        <div className="empty-state">
          <div className="empty-icon">📚</div>
          <h3>{t('series:emptyTitle')}</h3>
          <p>{t('series:emptyDescription')}</p>
          <button
            className="btn-primary"
            onClick={() => setIsCreating(true)}
          >
            {t('series:createFirst')}
          </button>
        </div>
      ) : (
        <div className="series-grid">
          {mySeries.map((series) => {
            const count = getCourseCount(series);
            return (
              <div
                key={series.id}
                className="series-card"
                onClick={() => navigate(`/creator/series/${series.id}`)}
              >
                <div className="series-thumbnail">
                  {getFirstCourseThumbnail(series) ? (
                    <img
                      src={getFirstCourseThumbnail(series) ?? ''}
                      alt=""
                    />
                  ) : (
                    <div className="thumbnail-placeholder">
                      <span>📖</span>
                    </div>
                  )}
                  <div className="series-count">
                    {count === 1
                      ? t('series:courseCountSingular', { count })
                      : t('series:courseCount', { count })}
                  </div>
                </div>
                <div className="series-info">
                  <h3>{series.title}</h3>
                  {series.description && (
                    <p className="series-description">{series.description}</p>
                  )}
                  <div className="series-meta">
                    {series.isPublished ? (
                      <span className="status-badge published">{t('series:published')}</span>
                    ) : (
                      <span className="status-badge draft">{t('series:draft')}</span>
                    )}
                  </div>
                </div>
                <button
                  className="btn-delete"
                  onClick={(e) => handleDeleteSeries(series.id, e)}
                  title={t('series:deleteSeries')}
                >
                  ×
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MySeriesPage;
